/**
 * Theme Utilities for applying brand theme colors
 */

/**
 * Apply theme colors to CSS custom properties
 * @param {Object} themeColors - Theme colors object from API
 */
export function applyThemeColors (themeColors) {
  if (!themeColors) return

  const root = document.documentElement

  // Apply all theme colors as CSS custom properties
  Object.keys(themeColors).forEach(key => {
    const value = themeColors[key]
    if (value) {
      // Convert snake_case to kebab-case for CSS custom properties
      const cssProperty = key.replace(/_/g, '-')
      root.style.setProperty(`--${cssProperty}`, value)
    }
  })

  // Also apply the mapped properties that are used in CSS
  const mappedStyles = createThemeStyles(themeColors)
  Object.keys(mappedStyles).forEach(key => {
    const value = mappedStyles[key]
    if (value) {
      root.style.setProperty(key, value)
    }
  })
}

/**
 * Get theme color value
 * @param {string} colorKey - The color key (e.g., 'primary_button_bg_color')
 * @param {Object} themeColors - Theme colors object
 * @param {string} fallback - Fallback color if not found
 * @returns {string} Color value
 */
export function getThemeColor (colorKey, themeColors, fallback = '#000000') {
  return themeColors && themeColors[colorKey] ? themeColors[colorKey] : fallback
}

/**
 * Create CSS styles object for inline styling
 * @param {Object} themeColors - Theme colors object
 * @returns {Object} Styles object
 */
export function createThemeStyles (themeColors) {
  if (!themeColors) return {}

  return {
    '--sidebar-background-color': themeColors.sidebar_bg_color || themeColors.sidbar_bg_color,
    '--sidebar-text-color': themeColors.sidebar_text_color || themeColors.sidbar_text_color,
    '--sidebar-hover-background-color': themeColors.sidebar_hover_bg_color || themeColors.sidebar_item_hover_bg_color,
    '--sidebar-hover-text-color': themeColors.sidebar_hover_text_color || themeColors.sidebar_item_hover_color,
    '--sidebar-item-color': themeColors.sidebar_item_color,
    '--sidebar-item-hover-color': themeColors.sidebar_item_hover_color,
    '--sidebar-item-active-color': themeColors.sidebar_item_active_color,
    '--sidebar-item-active-hover-color': themeColors.sidebar_item_active_hover_color,
    '--sidebar-item-hover-bg-color': themeColors.sidebar_item_hover_bg_color,
    '--header-background-color': themeColors.header_bg_color || themeColors.header_color,
    '--header-text-color': themeColors.header_text_color,
    '--header-border-color': themeColors.header_border_color,
    '--sub-header-color': themeColors.sub_header_color,
    '--card-1-color': themeColors.card_1_color,
    '--card-2-color': themeColors.card_2_color,
    '--card-3-color': themeColors.card_3_color,
    '--card-4-color': themeColors.card_4_color,
    '--card-5-color': themeColors.card_5_color,
    '--card-6-color': themeColors.card_6_color,
    '--primary-button-bg-color': themeColors.primary_button_bg_color,
    '--primary-button-text-color': themeColors.primary_button_text_color,
    '--success-button-bg-color': themeColors.success_button_bg_color,
    '--success-button-text-color': themeColors.success_button_text_color,
    '--danger-button-bg-color': themeColors.danger_button_bg_color,
    '--danger-button-text-color': themeColors.danger_button_text_color,
    '--button-background-color': themeColors.button_bg_color || themeColors.primary_button_bg_color,
    '--button-text-color': themeColors.button_text_color || themeColors.primary_button_text_color,
    '--button-border-color': themeColors.button_border_color,
    '--border-color': themeColors.border_color,
    '--border-hover-color': themeColors.border_hover_color,
    '--input-box-border-color': themeColors.input_box_border_color,
    '--input-box-text-color': themeColors.input_box_text_color,
    '--input-box-bg-color': themeColors.input_box_bg_color,
    '--input-box-hover-color': themeColors.input_box_hover_color,
    '--input-box-active-color': themeColors.input_box_active_color,
    '--navbar-text-color': themeColors.navbar_text_color || themeColors.header_text_color
  }
}

/**
 * Example CSS classes that can be used with theme colors
 */
export const themeCSSClasses = `
/* Example usage in your CSS */
.theme-sidebar {
  background-color: var(--sidbar-bg-color);
  color: var(--sidbar-text-color);
}

.theme-sidebar-item {
  color: var(--sidebar-item-color);
}

.theme-sidebar-item:hover {
  color: var(--sidebar-item-hover-color);
  background-color: var(--sidebar-item-hover-bg-color);
}

.theme-sidebar-item.active {
  color: var(--sidebar-item-active-color);
}

.theme-sidebar-item.active:hover {
  color: var(--sidebar-item-active-hover-color);
}

.theme-header {
  color: var(--header-color);
}

.theme-sub-header {
  color: var(--sub-header-color);
}

.theme-card-1 {
  background-color: var(--card-1-color);
}

.theme-card-2 {
  background-color: var(--card-2-color);
}

.theme-card-3 {
  background-color: var(--card-3-color);
}

.theme-card-4 {
  background-color: var(--card-4-color);
}

.theme-card-5 {
  background-color: var(--card-5-color);
}

.theme-card-6 {
  background-color: var(--card-6-color);
}

.theme-btn-primary {
  background-color: var(--primary-button-bg-color);
  color: var(--primary-button-text-color);
}

.theme-btn-success {
  background-color: var(--success-button-bg-color);
  color: var(--success-button-text-color);
}

.theme-btn-danger {
  background-color: var(--danger-button-bg-color);
  color: var(--danger-button-text-color);
}

.theme-border {
  border-color: var(--border-color);
}

.theme-border:hover {
  border-color: var(--border-hover-color);
}

.theme-input {
  border-color: var(--input-box-border-color);
  color: var(--input-box-text-color);
  background-color: var(--input-box-bg-color);
}

.theme-input:hover {
  border-color: var(--input-box-hover-color);
}

.theme-input:focus {
  border-color: var(--input-box-active-color);
}
`
